import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PaginationComponent } from '../../../inputs/pagination/pagination.component';
import { IInstitutionUser } from '../../../../pages/organization/settings/types/SettingsModuleTypes';
import { PromptCardComponent } from '../../../toasts-and-cards/prompt-card/prompt-card.component';
import { SettingsService } from '../../../../services/settings/settings.service';
import { UserProfileService } from '../../../../services/user/user-profile.service';
import { ToastService } from '../../../../services/toast/toast.service';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { IPagination } from '../../../../types/contactModule';
import { IRole } from '../../../../types/settingsDataModule';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { DataTableComponent } from '../../../ui/data-table/data-table.component';
import { StatusComponent } from '../../../ui/status/status.component';
import { EmptyStateComponent } from '../../../ui/empty-state/empty-state.component';
import { EditUserFormComponent } from '../../../forms/settingsforms/edit-user-form/edit-user-form.component';
import {
  ADMIN_ROLES,
  USER_STATE_STATUS,
  UserState,
  describeRole,
  formatRoleName,
  lockedRolesFor,
  userState,
  visibleRoles,
} from '../../../../pages/organization/settings/settings-roles';
import { formatWhen } from '../../../../../utils/sms';
import { failureReason } from '../../../../../utils/http-errors';

type FeedbackType = 'success' | 'error' | 'info' | 'warning';

/** One person as the table shows them. */
interface UserRow {
  user: IInstitutionUser;
  name: string;
  email: string;
  isSelf: boolean;
  isLastAdmin: boolean;
  state: UserState;
  statusText: string;
  status: string;
  roleLabel: string;
  roleExtra: number;
  roleDesc: string;
  roleAll: string;
  added: string;
}

@Component({
  selector: 'app-users-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTooltipModule,
    PaginationComponent,
    PromptCardComponent,
    HasPermissionDirective,
    DataTableComponent,
    StatusComponent,
    EmptyStateComponent,
    EditUserFormComponent,
  ],
  templateUrl: './users-table.component.html',
  styleUrls: ['../../../../pages/organization/settings/settings-ui.css', './users-table.component.css'],
})
export class UsersTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) users: IInstitutionUser[] = [];
  @Input() loading: boolean = false;
  @Input() params!: Record<string, any>;
  /** The current search term, for the empty state. */
  @Input() searchTerm = '';
  /** True when a status filter narrows the list. */
  @Input() filtered = false;
  /** True when every person in the organization is on this page (no search, filter or second page). */
  @Input() complete = false;
  /** The first list did not load: the empty state offers a retry instead of "no one yet". */
  @Input() loadFailed = false;
  /** Asked from the empty state. */
  @Output() addUser = new EventEmitter<void>();
  /** Asked from the load-failed empty state. */
  @Output() retry = new EventEmitter<void>();

  settingsService = inject(SettingsService);
  private userProfileService = inject(UserProfileService);
  private toast = inject(ToastService);
  private permissions = inject(PermissionsService);
  private destroyRef = inject(DestroyRef);

  /** Whether this person may add users, for the empty state's action and hint. */
  canCreate = false;

  allUsers: IInstitutionUser[] = [];
  rows: UserRow[] = [];
  usersParam = {};
  usersPage: number = 0;
  totalUsersPages: number = 1;
  totalUsers = 0;
  pageSize: number = 10;

  /** IAM roles, for plain-language descriptions. */
  private iamRoles: IRole[] = [];

  // Edit modal state
  showEditModal: boolean = false;
  editingUser: IInstitutionUser | null = null;
  editingIsSelf = false;
  editingLockedRoles: Record<string, string> = {};

  // Prompt card state
  showDisablePrompt: boolean = false;
  showEnablePrompt: boolean = false;
  showResetPasswordPrompt: boolean = false;
  selectedUser: IInstitutionUser | null = null;
  isDisablingUser: boolean = false;
  isEnablingUser: boolean = false;
  isResettingPassword: boolean = false;

  // Feedback goes to the toast rail
  feedbackMessage: string = '';
  feedbackType: FeedbackType = 'success';
  showFeedback: boolean = false;

  get currentUserId(): string {
    return this.userProfileService.userDetails?.sub || '';
  }

  get selectedUserName(): string {
    const user = this.selectedUser;
    return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.emailAddress || 'this person' : '';
  }

  /** The reset prompt reads as an invite for people who never finished signing up. */
  get selectedIsPending(): boolean {
    return userState(this.selectedUser) === 'pending';
  }

  get emptyMessage(): string {
    if (this.searchTerm.trim()) return `No one's first name contains “${this.searchTerm.trim()}”`;
    if (this.filtered) return 'No one matches this status';
    return 'No one has been added yet';
  }

  get emptyHint(): string {
    if (this.searchTerm.trim()) return 'Search looks at first names only. Check the spelling or try another name.';
    if (this.filtered) return 'Choose All in the status filter to see everyone.';
    return 'Add the people who will send messages, manage contacts or see reports.';
  }

  ngOnInit() {
    this.settingsService.getInstitutionUsersPagination
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: IPagination) => {
        this.usersPage = data.pageNumber;
        this.totalUsersPages = data.totalPages;
        this.totalUsers = data.totalElements;
      });

    this.permissions
      .hasPermission('USER_MANAGEMENT', 'create-users')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allowed) => (this.canCreate = allowed));

    this.settingsService.rolesData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((roles: IRole[]) => {
      this.iamRoles = roles || [];
      this.buildRows();
    });
  }

  ngOnChanges() {
    this.allUsers = this.users || [];
    this.usersParam = this.params;
    this.buildRows();
  }

  private buildRows() {
    const me = this.currentUserId;
    const enabledAdmins = this.allUsers.filter(
      (user) => user.enabled && (user.realmRoles || []).some((role) => ADMIN_ROLES.includes(role))
    );
    // Only provable when the whole team is on this page.
    const soleAdminId = this.complete && enabledAdmins.length === 1 ? enabledAdmins[0].id : null;

    this.rows = this.allUsers.map((user) => {
      const roles = visibleRoles(user.realmRoles);
      const state = userState(user);
      return {
        user,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'No name',
        email: user.emailAddress || user.email || '',
        isSelf: !!me && user.id === me,
        isLastAdmin: user.id === soleAdminId,
        state,
        statusText: USER_STATE_STATUS[state].text,
        status: USER_STATE_STATUS[state].status,
        roleLabel: roles.length ? formatRoleName(roles[0]) : 'No role',
        roleExtra: Math.max(0, roles.length - 1),
        roleDesc: roles.length ? describeRole(roles[0], this.iamRoles) : 'Cannot use any part of the console yet.',
        roleAll: roles.map(formatRoleName).join(', '),
        added: user.createdTimestamp ? formatWhen(new Date(user.createdTimestamp)) : '—',
      };
    });
  }

  get updatedParams() {
    // `size` and `pageSize` travel together so a page-size change is never half applied.
    return { ...this.usersParam, page: this.usersPage, size: this.pageSize, pageSize: this.pageSize };
  }

  updateUsersPage(newPage: number) {
    this.usersPage = newPage;
    this.settingsService.fetchInstitutionUsers(this.updatedParams);
  }

  goToNextPage() {
    this.usersPage += 1;
    this.settingsService.fetchInstitutionUsers(this.updatedParams);
  }

  goToPreviousPage() {
    this.usersPage -= 1;
    this.settingsService.fetchInstitutionUsers(this.updatedParams);
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.usersPage = 0;
    this.settingsService.fetchInstitutionUsers(this.updatedParams);
  }

  get usersLoading() {
    return this.settingsService.isLoading || this.settingsService.isFetching;
  }

  onEditClick(user: IInstitutionUser) {
    const row = this.rows.find((r) => r.user.id === user.id);
    this.editingIsSelf = !!row?.isSelf;
    this.editingLockedRoles = lockedRolesFor(user.realmRoles, {
      isSelf: !!row?.isSelf,
      isLastAdmin: !!row?.isLastAdmin,
    });
    this.editingUser = user;
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editingUser = null;
  }

  onEditSuccess() {
    this.closeEditModal();
    this.displayFeedback('Changes saved.', 'success');
  }

  /** Why disabling this person is blocked, or '' when it is allowed. */
  disableBlockedReason(row: UserRow): string {
    if (row.isSelf) return 'You cannot disable your own account';
    if (row.isLastAdmin) return 'The only administrator cannot be disabled';
    return '';
  }

  onDisableClick(user: IInstitutionUser) {
    const row = this.rows.find((r) => r.user.id === user.id);
    if (row && this.disableBlockedReason(row)) return;
    this.selectedUser = user;
    this.showDisablePrompt = true;
  }

  onEnableClick(user: IInstitutionUser) {
    this.selectedUser = user;
    this.showEnablePrompt = true;
  }

  closeDisablePrompt() {
    this.showDisablePrompt = false;
    this.selectedUser = null;
  }

  closeEnablePrompt() {
    this.showEnablePrompt = false;
    this.selectedUser = null;
  }

  onResetPasswordClick(user: IInstitutionUser) {
    this.selectedUser = user;
    this.showResetPasswordPrompt = true;
  }

  closeResetPasswordPrompt() {
    this.showResetPasswordPrompt = false;
    this.selectedUser = null;
  }

  confirmDisableUser() {
    if (!this.selectedUser?.id) {
      return;
    }

    const userName = this.selectedUserName;
    this.isDisablingUser = true;

    this.settingsService.disableUser(this.selectedUser.id).subscribe({
      next: () => {
        this.isDisablingUser = false;
        this.closeDisablePrompt();
        this.displayFeedback(`${userName} can no longer sign in.`, 'success');
      },
      error: (error) => {
        this.isDisablingUser = false;
        this.closeDisablePrompt();
        const errorMessage = `${userName} was not disabled. ${failureReason(error, 'That account no longer exists. Refresh the list.')}`;
        this.displayFeedback(errorMessage, 'error');
      },
    });
  }

  confirmEnableUser() {
    if (!this.selectedUser?.id) {
      return;
    }

    const userName = this.selectedUserName;
    this.isEnablingUser = true;

    this.settingsService.enableUser(this.selectedUser.id).subscribe({
      next: () => {
        this.isEnablingUser = false;
        this.closeEnablePrompt();
        this.displayFeedback(`${userName} can sign in again.`, 'success');
      },
      error: (error) => {
        this.isEnablingUser = false;
        this.closeEnablePrompt();
        const errorMessage = `${userName} was not enabled. ${failureReason(error, 'That account no longer exists. Refresh the list.')}`;
        this.displayFeedback(errorMessage, 'error');
      },
    });
  }

  confirmResetPassword() {
    if (!this.selectedUser?.emailAddress) {
      return;
    }

    const userName = this.selectedUserName;
    const userEmail = this.selectedUser.emailAddress;
    const pending = this.selectedIsPending;
    this.isResettingPassword = true;

    this.settingsService.requestPasswordReset(userEmail).subscribe({
      next: () => {
        this.isResettingPassword = false;
        this.closeResetPasswordPrompt();
        this.displayFeedback(
          pending
            ? `Invite sent again to ${userEmail}.`
            : `Password reset link sent to ${userEmail}.`,
          'success'
        );
      },
      error: (error) => {
        this.isResettingPassword = false;
        this.closeResetPasswordPrompt();
        const errorMessage = `The email to ${userName} was not sent. ${failureReason(error, 'That account no longer exists. Refresh the list.')}`;
        this.displayFeedback(errorMessage, 'error');
      },
    });
  }

  displayFeedback(message: string, type: FeedbackType) {
    this.feedbackMessage = message;
    this.feedbackType = type;
    this.showFeedback = true;
    if (type === 'error' || type === 'warning') {
      this.toast.error(message);
    } else {
      this.toast.success(message);
    }
  }

  formatRole(role: string): string {
    return formatRoleName(role);
  }

  ngOnDestroy() {
    // Subscriptions end with the component (takeUntilDestroyed).
  }
}
